import Api from "@/_helpers/Api"
import { useEffect, useState, useRef } from "react"
import Skeleton from "react-loading-skeleton"
import { trans } from "@/_providers/Translation"
import GroupedBarChart from "../../../partials/GroupedBarChart"
import { getPaletteColor } from "../../../partials/chartTheme"
import { Button } from "@mui/material"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DateRange from "@/components/partials/DateRange"
import filterImage from "@/assets/img/icons/filter.svg"

const INITIAL_FILTERS = {
  from: null,
  to: null,
}

export default function FinancesMain() {
  const statusColors = {
    incomes: getPaletteColor(1).color1,
    expenses: getPaletteColor(2).color1,
    vat: getPaletteColor(0).color1,
    tax: getPaletteColor(1).color1,
  }

  const statusLabels = {
    incomes: trans("random.income"),
    expenses: trans("random.expense"),
    vat: trans("labels.dds"),
    tax: trans("random.profitTax"),
  }

  const [state, setState] = useState({
    loading: true,
    data: [],
  })
  const [filters, setFilters] = useState(INITIAL_FILTERS)

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      loadData()
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters])

  const loadData = () => {
    setState((prev) => ({ ...prev, loading: true }))
    Api.get(`accounts/yearly-summary?from=${filters.from || ''}&to=${filters.to || ''}`)
      .then((res) => {
        setState((prev) => ({ ...prev, data: res.data || [] }))
      })
      .finally(() => {
        setState((prev) => ({ ...prev, loading: false }))
      })
  }

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function getAppliedFiltersCount() {
    return filters.from || filters.to ? 1 : 0
  }

  const currencyName = state.data[0]?.currency?.name ?? ""

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "—"
    if (value >= 1_000_000)
      return `${(value / 1_000_000).toFixed(1)}M ${currencyName}`
    if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K ${currencyName}`
    return `${Number(value).toFixed(2)} ${currencyName}`
  }

  const labels = state.data.map((d) => String(d.year))

  const tooltipRow = (label, value) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 24,
        color: "#fff",
        padding: "2px 0",
      }}
    >
      <span style={{ color: "#94a3b8" }}>{label}</span>
      <strong style={{ color: "#fff" }}>{formatCurrency(value)}</strong>
    </div>
  )

  const series = [
    {
      name: statusLabels.incomes,
      color1: getPaletteColor(1).color1,
      color2: getPaletteColor(1).color2,
      data: state.data.map((d) => d.incomes || 0),
      tooltipContent: (i) => {
        const item = state.data[i]
        const methods = item?.paymentMethods
        if (!methods) return tooltipRow(statusLabels.incomes, item?.incomes)
        return (
          <div
            style={{
              borderTop: "1px solid #334155",
              paddingTop: 6,
              marginTop: 2,
            }}
          >
            {tooltipRow("PayPal", methods.paypal)}
            {tooltipRow("ePay", methods.epay)}
            {tooltipRow(trans("random.bankTransfer"), methods.bank_transfer)}
            {tooltipRow(trans("random.cash"), methods.cash)}
            {tooltipRow(trans("random.byCard"), methods.card)}
            <div
              style={{
                borderTop: "1px solid #334155",
                marginTop: 6,
                paddingTop: 6,
              }}
            >
              {tooltipRow(trans("random.total").toUpperCase(), methods.total)}
            </div>
          </div>
        )
      },
    },
    {
      name: statusLabels.expenses,
      color1: getPaletteColor(2).color1,
      color2: getPaletteColor(2).color2,
      data: state.data.map((d) => d.expenses || 0),
      valueFormatter: formatCurrency,
    },
    {
      name: statusLabels.vat,
      color1: getPaletteColor(0).color1,
      color2: getPaletteColor(0).color2,
      data: state.data.map((d) => d.vat || 0),
      valueFormatter: formatCurrency,
    },
    {
      name: statusLabels.tax,
      color1: getPaletteColor(1).color1,
      color2: getPaletteColor(1).color2,
      data: state.data.map((d) => d.tax || 0),
      valueFormatter: formatCurrency,
    },
  ]

  return (
    <div className="todos-stats-chart">
      <div className="head">
        <h3>{trans("random.finances")}</h3>
        <div className="right">
          <ul>
            {Object.entries(statusLabels).map(([status, label]) => (
              <li key={status}>
                <span style={{ backgroundColor: statusColors[status] }} />
                {label}
              </li>
            ))}
          </ul>
          <Button onClick={(e) => {
              compactFilterLayoutRef.current.open(e)
            }}>
              <img src={filterImage} alt="Filter" />
              {getAppliedFiltersCount() ?
                <i>{getAppliedFiltersCount()}</i>
                : <></>}
            </Button>
            <CompactFilterLayout
              ref={compactFilterLayoutRef}
              appliedFiltersCount={getAppliedFiltersCount()}
              onClearClick={() => setFilters(INITIAL_FILTERS)}
            >
              <div className='row'>
                <div className='col'>
                  <DateRange
                    startDate={filters.from || ''}
                    endDate={filters.to || ''}
                    onStartDateChange={(date) => handleFilterChange('from', date)}
                    onEndDateChange={(date) => handleFilterChange('to', date)}
                    yearOnly
                  />
                </div>
              </div>
            </CompactFilterLayout>
        </div>
      </div>
      <hr />
      {state.loading ? (
        <Skeleton height={360} count={1} />
      ) : (
        <GroupedBarChart
          labels={labels}
          series={series}
          barWidth={33}
          roundedBottom
        />
      )}
    </div>
  )
}
